import React, { PureComponent } from 'react';

import { observer } from 'mobx-react';
import { NavigateFunction } from 'react-router';
import withNavigateParams from '../../utils/withNavigateParams';
import { observable } from 'mobx';
import toaster from '../../stores/toaster';
import { Button, Menu, Spin, Tabs, Typography } from 'antd';
import {
	UploadOutlined,
	DeleteOutlined,
	EditOutlined,
	UserOutlined,
	PauseOutlined,
	CrownOutlined,
} from '@ant-design/icons';
import { TPage, TPContent, TPSidebar, TPSidebarHeader } from '../../controls/TPage';
import Avatar from 'antd/lib/avatar/avatar';
import AvatarUploadModal from '../../global-modals/AvatarUploadModal';
import dataUriToBlob from '../../utils/dataUriToBlob';
import { StatDiv, StatHeader, StatPlate, StatPlateTitle, StatPlateValue } from '../../controls/StatHeader';
import PromptModal from '../../global-modals/PromptModal';
import handleError from '../../utils/handleError';
import ConfirmModal from '../../global-modals/ConfirmModal';
import BackButton from '../../controls/BackButton';
import {
	EmployeeRowExt,
	manager_deleteEmployee,
	manager_fetchEmployee,
	manager_fetchEmployeeReviews,
	manager_fetchEmployeeTransactions,
	manager_updateEmployeeDream,
	manager_updateEmployeeName,
	manager_updateEmployeeTextRole,
	manager_uploadEmployeeAvatar,
} from '../../lib/api-web/employees';
import { autobind } from 'core-decorators';
import { Sorting } from '../../web-types/Sorting';
import { TableController } from '../../controls/TableController';
import formatMoney from '../../utils/formatMoney';
import moment from 'moment';

interface ChainEmployeeCardProps {
	navigate: NavigateFunction;
	params: { placeId: string; employeeId: string };
	state: { backable: boolean } | null;
}

const DEFAULT_DREAM = 'Saving up for a new apartment in Burj Khalifa';

@observer
class ChainEmployeeCard extends PureComponent<ChainEmployeeCardProps> {
	@observable loading = false;
	@observable avatarUploading = false;
	@observable nameEditing = false;
	@observable employeeDeleting = false;
	@observable imageUrl: string | null = null;
	@observable data: EmployeeRowExt | null = null;
	@observable reviewsContentVisible = false;
	@observable reviewsTotal: number | null = null;
	@observable transactionsContentVisible = false;
	@observable transactionsTotal: number | null = null;

	@autobind
	handleReviewsTablePropsUpdate(reviewsContentVisible: boolean, reviewsTotal: number | null, rowsPerPage: number) {
		this.reviewsContentVisible = reviewsContentVisible;
		this.reviewsTotal = reviewsTotal;
	}

	@autobind
	handleTransactionsTablePropsUpdate(
		transactionsContentVisible: boolean,
		transactionsTotal: number | null,
		rowsPerPage: number,
	) {
		this.transactionsContentVisible = transactionsContentVisible;
		this.transactionsTotal = transactionsTotal;
	}

	async reload() {
		this.loading = true;
		try {
			const result = await manager_fetchEmployee(
				Number(this.props.params.placeId),
				Number(this.props.params.employeeId),
			);
			if (result.result) {
				this.data = result.data;
			} else {
				toaster.show({
					type: 'error',
					message: result.error,
				});
			}
		} catch (err) {
			toaster.show({
				type: 'error',
				message: (err as Error).message,
			});
		}
		this.loading = false;
	}

	componentDidMount() {
		this.reload();
	}

	componentDidUpdate(prevProps: ChainEmployeeCardProps) {
		if (prevProps.params.placeId !== this.props.params.placeId) {
			this.reload();
		}
	}

	handleClick(info: any) {
		console.log('info: ', info);
	}

	async handleAvatarUpload() {
		const newAvatarDataUri = await AvatarUploadModal.prompt();
		if (newAvatarDataUri) {
			this.avatarUploading = true;
			try {
				await manager_uploadEmployeeAvatar(
					Number(this.props.params.placeId),
					Number(this.props.params.employeeId),
					await dataUriToBlob(newAvatarDataUri),
				);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.avatarUploading = false;
		}
	}

	async handleEditName() {
		const newFirstName = await PromptModal.prompt('Enter employee first name', this.data!.firstName);
		if (newFirstName) {
			const newLastName = await PromptModal.prompt('Enter employee last name', this.data!.lastName);
			if (newLastName) {
				this.nameEditing = true;
				try {
					await manager_updateEmployeeName(
						Number(this.props.params.placeId),
						Number(this.props.params.employeeId),
						newFirstName,
						newLastName,
					);
					await this.reload();
				} catch (err) {
					handleError(err);
				}
				this.nameEditing = false;
			}
		}
	}

	@autobind
	async handleEditTextRole() {
		const newTextRole = await PromptModal.prompt(
			'Enter employee role in your venue',
			this.data!.up_text_role || 'Waiter',
		);
		if (newTextRole) {
			this.nameEditing = true;
			try {
				await manager_updateEmployeeTextRole(
					Number(this.props.params.placeId),
					Number(this.props.params.employeeId),
					newTextRole,
				);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.nameEditing = false;
		}
	}

	@autobind
	async handleEditDream() {
		const newDream = await PromptModal.prompt('Enter employee dream', this.data!.up_dream || DEFAULT_DREAM);
		if (newDream) {
			this.nameEditing = true;
			try {
				await manager_updateEmployeeDream(
					Number(this.props.params.placeId),
					Number(this.props.params.employeeId),
					newDream,
				);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.nameEditing = false;
		}
	}

	async handleDelete() {
		const descision = await ConfirmModal.prompt(
			'Are you sure?',
			'This action is irreversible. You will lose all the data: employees, transactions, QR codes. Are you absolutely sure?',
			'Yes',
		);
		if (!descision) {
			return;
		}
		this.employeeDeleting = true;
		try {
			const response = await manager_deleteEmployee(
				Number(this.props.params.placeId),
				Number(this.props.params.employeeId),
			);
			if (response.result) {
				this.employeeDeleting = false;
				toaster.show({ message: 'You successfully deleted the employee', type: 'success' });
				this.props.navigate(`/${Number(this.props.params.placeId)}/employees/`);
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
	}

	@autobind
	async handleLoadReviews(params: { query: string }, offset: number, limit: number, sorting: Sorting | null) {
		return manager_fetchEmployeeReviews(
			Number(this.props.params.placeId),
			Number(this.props.params.employeeId),
			offset,
			limit,
			{
				query: params.query,
				sorting,
			},
		);
	}

	@autobind
	async handleLoadTransactions(params: void, offset: number, limit: number, sorting: Sorting | null) {
		return manager_fetchEmployeeTransactions(
			Number(this.props.params.placeId),
			Number(this.props.params.employeeId),
			offset,
			limit,
			{
				sorting,
			},
		);
	}

	render() {
		if (!this.data) {
			return <Spin />;
		}

		return (
			<div className="page">
				<div className="page-head">
					<div className="page-title">
						<Typography.Title
							level={4}
							style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
						>
							<BackButton props={this.props} /> Employee {this.data.firstName} {this.data.lastName},{' '}
							{this.data.up_text_role}
						</Typography.Title>
					</div>
					<div className="page-actions"></div>
				</div>
				<TPage className="section">
					<TPSidebar>
						<TPSidebarHeader>
							{this.avatarUploading ? (
								<Avatar size={120} src={null} icon={<Spin />} />
							) : (
								<Avatar size={120} src={this.data.avatarUrl} icon={<UserOutlined />} />
							)}
							<div style={{ marginTop: 15 }}>
								{this.data.phone} <Button type="dashed" size="small" icon={<EditOutlined />} />
							</div>
							<div
								style={{
									marginTop: 15,
									textAlign: 'center',
									paddingLeft: 7,
									paddingRight: 20,
									marginBottom: -15,
								}}
							>
								<i style={{ fontSize: 11 }}>{this.data!.up_dream || DEFAULT_DREAM}</i>
								<br />
								<Button
									onClick={this.handleEditDream}
									size="small"
									style={{ fontSize: 11 }}
									type="link"
									icon={<EditOutlined />}
								>
									Edit dream
								</Button>
								{/* <Button type="dashed" size="small" icon={<EditOutlined />} /> */}
							</div>
						</TPSidebarHeader>
						<Menu inlineIndent={12} selectable={false} onClick={this.handleClick} mode="inline">
							<Menu.Item
								key="1"
								icon={<UploadOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleAvatarUpload();
								}}
							>
								Upload new avatar
							</Menu.Item>
							<Menu.Item
								key="2"
								icon={this.nameEditing ? <Spin /> : <EditOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleEditName();
								}}
							>
								Edit name
							</Menu.Item>
							<Menu.Item
								key="3"
								icon={this.nameEditing ? <Spin /> : <CrownOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleEditTextRole();
								}}
							>
								Edit role
							</Menu.Item>
							<Menu.Item
								key="4"
								icon={this.nameEditing ? <Spin /> : <PauseOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleEditName();
								}}
							>
								Pause employee
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item
								key="5"
								danger
								icon={<DeleteOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleDelete();
								}}
							>
								Remove employee
							</Menu.Item>
						</Menu>
					</TPSidebar>
					<TPContent>
						<StatHeader>
							<StatPlate>
								<StatPlateTitle>Rating for last 7 days</StatPlateTitle>
								<StatPlateValue>
									{parseFloat(parseFloat(String(this.data.rating_7d)).toFixed(2))}
								</StatPlateValue>
							</StatPlate>
							<StatDiv />
							<StatPlate>
								<StatPlateTitle>Rating for current month</StatPlateTitle>
								<StatPlateValue>
									{parseFloat(parseFloat(String(this.data.rating_mnth)).toFixed(2))}
								</StatPlateValue>
							</StatPlate>
							<StatDiv />
							<StatPlate>
								<StatPlateTitle>Rating for current week</StatPlateTitle>
								<StatPlateValue>
									{parseFloat(parseFloat(String(this.data.rating_week)).toFixed(2))}
								</StatPlateValue>
							</StatPlate>
						</StatHeader>
						<Tabs defaultActiveKey="1" type="card">
							<Tabs.TabPane tab="Tips &amp; Reviews" key="2">
								<div style={{ paddingLeft: 10 }}>
									<TableController
										noSection
										onRowClick={({ id }) => {
											this.props.navigate(`/${this.props.params.placeId}/employees/${id}/`, {
												state: { backable: true },
											});
										}}
										params={{ query: '' }}
										load={this.handleLoadReviews}
										onTablePropsUpdate={this.handleReviewsTablePropsUpdate}
										columns={[
											{
												key: 'rating',
												title: '',
												render: value => (
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontSize: 20,
															width: 40,
															height: 40,
															borderRadius: 20,
															background: value === 4 ? '#deeec9' : '#c9eec9',
															border: '1px solid #e0e0e0',
														}}
													>
														<span style={{ color: value === 4 ? '#818f00' : '#008f07' }}>
															{value}
														</span>
													</div>
												),
												width: 50,
											},
											{
												key: 'text',
												title: 'Review',
												maxWidth: 700,
												minWidth: 100,
												sortable: true,
												render: value => value || <i>No text</i>,
											},
											{
												key: 'incomingTx.amount',
												title: 'Tip left',
												minWidth: 100,
												sortable: true,
												render: (value, row) => formatMoney(value, 'AED'),
											},
											{
												key: 'created_at',
												title: 'Date',
												sortable: true,
												render: value => <>{moment(value).format('DD.MM HH:mm')}</>,
											},
										]}
									/>
								</div>
							</Tabs.TabPane>
							<Tabs.TabPane tab="Transactions" key="4">
								<div style={{ paddingLeft: 10 }}>
									<TableController
										noSection
										params={0 as unknown as void}
										load={this.handleLoadTransactions}
										onTablePropsUpdate={this.handleTransactionsTablePropsUpdate}
										columns={[
											{
												key: 'incomingTx.amount',
												title: 'Total amount',
												minWidth: 100,
												sortable: true,
												render: (value, row) => formatMoney(value, 'AED'),
											},
											{
												key: 'amount',
												title: 'Distributed amount',
												minWidth: 100,
												sortable: true,
												render: (value, row) => formatMoney(value, 'AED'),
											},
											{
												key: 'percent',
												title: 'Percent',
												minWidth: 100,
												sortable: true,
												render: (value, row) => (value * 100).toFixed(2) + '%',
											},
											{
												key: 'created_at',
												title: 'Date',
												sortable: true,
												render: value => <>{moment(value).format('DD.MM HH:mm')}</>,
											},
										]}
									/>
								</div>
							</Tabs.TabPane>
							{/* <Tabs.TabPane tab="Assigned QRs" key="3">
								<div style={{ paddingLeft: 10 }}>
									<TipzTable
										sorting={null}
										onSortingChange={s => null}
										loading={this.loading}
										head={[
											{
												key: 'avg_rate',
												title: 'QR ID',
												sortable: true,
												render: (value, row) => <>{row.qrId}</>,
											},
											{
												key: 'qr',
												title: 'Personal card',
												render: v => (
													<Button
														onClick={e => {
															e.preventDefault();
															QRModal.prompt(
																'Employee QR code',
																`https://q.tipz.ae/${this.props.params.placeId}/${this.data?.up_unique_user_id}/1/100`,
															);
														}}
													>
														Show
													</Button>
												),
											},
										]}
										rows={[
											{
												qrId: 342,
												avatarUrl:
													'data:image/jpeg;base64,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',
												name: 'Denis Al Aaradzh',
												rating: 5,
												text_role: `Amaazing food! Great Service!
								The whole experience from start to finish is great staff is always so friendly and kind.
								The food can’t get better and the prices are fair for the portion size.
								I think it has the best view and atmosphere in Dubai.`,
												qr:
													Math.random() +
													'' +
													Math.random() +
													'' +
													Math.random() +
													'' +
													Math.random(),
												avg_rate_7d: 3.5 + Math.random() * 1.5,
												avg_rate_p7d: 3.5 + Math.random() * 1.5,
												tips_n_reviews_count_7d: Math.floor(10 + Math.random() * 30),
												tips_n_reviews_count_p7d: Math.floor(10 + Math.random() * 30),
												phone: '+97112345678',
											},
											{
												qrId: 754,
												avatarUrl:
													'data:image/jpeg;base64,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',
												name: 'John Mayer',
												rating: 5,
												text_role:
													'I would like to give five stars for this amazing restaurant, the service is brilliant, unbelievable experience, staff are so friendly and helpful perfect location for any kind of events, delicious mouth watering foods, what more can I say !! You won’t regret coming there , the place located in souq al bahar, exactly near to place hotel downtown. Easy parking access as well .',
												qr:
													Math.random() +
													'' +
													Math.random() +
													'' +
													Math.random() +
													'' +
													Math.random(),
												avg_rate_7d: 3.5 + Math.random() * 1.5,
												avg_rate_p7d: 3.5 + Math.random() * 1.5,
												tips_n_reviews_count_7d: Math.floor(10 + Math.random() * 30),
												tips_n_reviews_count_p7d: Math.floor(10 + Math.random() * 30),
												phone: '+971847294728',
											},
										]}
									/>
								</div>
							</Tabs.TabPane> */}
						</Tabs>
					</TPContent>
				</TPage>
			</div>
		);
	}
}

export default withNavigateParams(ChainEmployeeCard);
